import React, { useContext, useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components';
import { currencyFormat, uniquePrestaPiece } from '../../../utils/utils';
import { getAuthParameters, API_BASE_URL } from '../../../utils/api';
// Components
import Box from '../../shared/atoms/Box';
import Text from '../../shared/atoms/Text';
import Link from '../../shared/atoms/Link';
import Button from '../../shared/atoms/Button';
import Separator from '../../Separator';
import CourseSectionHeading from './CourseSectionHeading';
import CurrentAppointmentBlock from './CurrentAppointmentBlock';
import BrandIcon from './BrandIcon';
// Icons
import Download16 from '../../icons/Download16';
import { yieldBonusType } from '../../../utils/slots';
import { ToastCtx } from '../../../contexts/ToastContext';
import { RJ } from '../../rapport/rapportTokens';

// §5.2 — "Cette intervention" : the green "Payé" banner + the priced lines +
// the facture row, page-scoped to the brand charte (RJ tokens).
const Lines = styled(Box)`
  border: 1px solid ${RJ.border};
  border-radius: ${RJ.radiusLg};
`;

// Facture row stacks below 560px (spec §8) so the amount + link never cramp.
const FactureRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;

  @media (max-width: 560px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const CourseBill = ({ course }) => {
  const { t } = useTranslation();
  const { showToast } = useContext(ToastCtx);
  const [paymentLink, setPaymentLink] = useState(null);
  const [loadingPaymentLink, setLoadingPaymentLink] = useState(false);
  const prestations = Object.values(uniquePrestaPiece(course.prestations));
  const pieces = Object.values(uniquePrestaPiece(course.pieces));

  const getBill = async () => {
    const courseid = course.id;
    const auth = getAuthParameters();
    if (auth) {
      try {
        const url = `${API_BASE_URL}/courses/${courseid}/invoice?ctk=${encodeURIComponent(
          auth.token,
        )}&cti=${auth.id}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        });

        if (response.ok) {
          showToast({
            title: t('invoice.sent'),
            description: t('invoice.sent.description'),
            type: 'success.main',
          });
        } else {
          throw new Error('Invoice request failed');
        }
      } catch (error) {
        showToast({
          title: t('invoice.error'),
          description: t('invoice.error.description'),
          type: 'danger.main',
        });
      }
    }
  };

  const fetchPaymentLink = async () => {
    const auth = getAuthParameters();
    if (auth && !paymentLink) {
      setLoadingPaymentLink(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/clients/${auth.id}/courses/${course.id}/payment-link`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${auth.token}`,
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          },
        );

        const data = await response.json();
        if (data.success && data.payment_link) {
          setPaymentLink(data.payment_link);
        }
      } catch (error) {
        console.error('Error fetching payment link:', error);
      } finally {
        setLoadingPaymentLink(false);
      }
    }
  };

  useEffect(() => {
    if (!course.stripe_payment_id) {
      fetchPaymentLink();
    }
  }, [course.stripe_payment_id]);
  return (
    <Box mb={9}>
      <CourseSectionHeading
        icon={<BrandIcon name="cette-intervention" size={28} />}>
        {t('course.intervention.heading')}
      </CourseSectionHeading>
      <Box mb={4}>
        <CurrentAppointmentBlock course={course} />
      </Box>
      <Lines p={{ xs: 4, lg: 5 }}>
        {prestations.map((prestation, index) => {
          return (
            <Box
              key={prestation.prestaPiece.id}
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              mt={index == 0 ? 0 : 2}>
              <Box flexDirection="row" alignItems="center">
                <Box
                  borderColor="gray.light"
                  borderWidth="1px"
                  borderRadius="xxs"
                  borderStyle="solid"
                  width={21}
                  height={21}
                  alignItems="center"
                  justifyContent="center"
                  mr={2}>
                  {prestation.nb}
                </Box>
                <Text>{prestation.prestaPiece.nom}</Text>
              </Box>
              <Box ml={4}>
                <Text>
                  {currencyFormat(
                    prestation.prestaPiece.prix * prestation.nb,
                    prestation.prestaPiece.currency,
                  )}
                </Text>
              </Box>
            </Box>
          );
        })}
        {pieces.map((prestation) => {
          return (
            <Box
              key={prestation.prestaPiece.id}
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              mt={2}>
              <Box flexDirection="row" alignItems="center">
                <Box
                  borderColor="gray.light"
                  borderWidth="1px"
                  borderRadius="xxs"
                  borderStyle="solid"
                  width={21}
                  height={21}
                  alignItems="center"
                  justifyContent="center"
                  mr={2}>
                  {prestation.nb}
                </Box>
                <Text>{prestation.prestaPiece.modele}</Text>
              </Box>
              <Box ml={4}>
                <Text>
                  {currencyFormat(
                    prestation.prestaPiece.prix_vente *
                      prestation.nb *
                      prestation.nb,
                    prestation.prestaPiece.currency,
                  )}
                </Text>
              </Box>
            </Box>
          );
        })}
        {!!course.fee_service && (
          <Box flexDirection="row" justifyContent="space-between" mt={2}>
            <Text>Frais de service</Text>
            <Text fontWeight={2}>{course.fee_service}€</Text>
          </Box>
        )}
        {!!course.fee_delivery && (
          <Box flexDirection="row" justifyContent="space-between" mt={2}>
            <Text>Frais de déplacement</Text>
            <Text fontWeight={2}>{course.fee_delivery}€</Text>
          </Box>
        )}
        {!!course.yield_bonus && (
          <Box flexDirection="row" justifyContent="space-between" mt={2}>
            <Text>Supplément créneau {yieldBonusType(course.date)}</Text>
            <Text fontWeight={2}>{course.yield_bonus}€</Text>
          </Box>
        )}
        <Text textAlign="right" mt={4} color="black">
          <Trans
            i18nKey="coursebill.texts.1"
            components={{ strong: <strong /> }}
            values={{
              prix: course.prix + course.reduction,
            }}
          />
        </Text>
        <Separator axe="horizontal" my={4} />
        <Box>
          <Box flexDirection="row" justifyContent="space-between">
            <Text color="gray.darker">{t('coursebill.texts.2')}</Text>
            <Text fontWeight={2} color="gray.darker">
              {currencyFormat(course.tip, course.currency)}
            </Text>
          </Box>
          {!!course.reduction && (
            <Box flexDirection="row" justifyContent="space-between">
              <Text>{t('reduction')}</Text>
              <Text fontWeight={2}>- {course.reduction}€</Text>
            </Box>
          )}
        </Box>
        <Separator axe="horizontal" my={4} />
        <FactureRow>
          {course.stripe_payment_id ? (
            <Link
              before={<Download16 />}
              color="blue.electric"
              fontWeight="bold"
              onClick={() => getBill()}>
              {t('invoice.download')}
            </Link>
          ) : (
            <Button
              variant="primary"
              onClick={() => (window.location.href = paymentLink)}
              disabled={loadingPaymentLink || !paymentLink}>
              {loadingPaymentLink ? t('payment.loading') : t('payment.online')}
            </Button>
          )}
          <Text color="black" fontSize="lg">
            <Trans
              i18nKey="coursebill.texts.3"
              values={{
                prix: course.prix,
              }}
            />
          </Text>
        </FactureRow>
      </Lines>
    </Box>
  );
};

export default CourseBill;
